// ============================================================================
// UNDOPLACEMENT.JS — 10-second undo window after placing a crafted object
// (pi-tx9.8)
//
// When OBJECT_PLACED fires, shows a floating undo button with a 10-second
// countdown timer. Clicking it within the window removes the object from
// the scene and state, refunds crafting resources, and emits OBJECT_REMOVED.
// ============================================================================

import { getRecipe } from '../systems/crafting.js';
import { addToInventory } from '../systems/resources.js';
import { getGameState, saveGameState } from '../systems/state.js';
import { eventBus, GameEvents } from '../systems/events.js';

// ---------------------------------------------------------------------------
// Module state
// ---------------------------------------------------------------------------
let undoContainer = null;
let undoTimer = null;
let animationFrameId = null;
let countdownBar = null;
let currentUndo = null; // { mesh, recipeId, id }

const UNDO_DURATION_MS = 10000;

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------
function injectStyles() {
  const style = document.createElement('style');
  style.textContent = `
    /* ---- Undo button container ---- */
    #undo-placement-container {
      position: fixed;
      top: 16px;
      left: 50%;
      transform: translateX(-50%);
      z-index: 950;
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 4px;
      pointer-events: none;
      opacity: 0;
      transition: opacity 0.3s ease;
    }
    #undo-placement-container.undo-visible {
      opacity: 1;
      pointer-events: auto;
    }
    #undo-placement-container.undo-fading {
      opacity: 0;
      pointer-events: none;
    }

    /* ---- Undo button ---- */
    #undo-placement-btn {
      display: flex;
      align-items: center;
      gap: 6px;
      background: linear-gradient(135deg, #f5e6c8 0%, #e8d5a3 100%);
      border: 2px solid #c4a265;
      border-radius: 12px;
      padding: 8px 16px;
      font-family: 'Georgia', serif;
      font-size: 14px;
      color: #5a3e1b;
      cursor: pointer;
      box-shadow: 0 2px 12px rgba(0, 0, 0, 0.2);
      user-select: none;
      -webkit-tap-highlight-color: transparent;
      transition: transform 0.15s ease, box-shadow 0.15s ease;
    }
    #undo-placement-btn:hover {
      transform: scale(1.05);
      box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3);
    }
    #undo-placement-btn:active {
      transform: scale(0.95);
    }

    #undo-placement-icon {
      font-size: 16px;
      line-height: 1;
    }

    /* ---- Countdown bar ---- */
    #undo-countdown-track {
      width: 120px;
      height: 4px;
      background: rgba(196, 162, 101, 0.3);
      border-radius: 2px;
      overflow: hidden;
    }
    #undo-countdown-bar {
      width: 100%;
      height: 100%;
      background: linear-gradient(90deg, #c4a265, #a0853a);
      border-radius: 2px;
      transform-origin: left center;
      transition: none;
    }
  `;
  document.head.appendChild(style);
}

// ---------------------------------------------------------------------------
// DOM creation
// ---------------------------------------------------------------------------
function createDOM() {
  undoContainer = document.createElement('div');
  undoContainer.id = 'undo-placement-container';

  const btn = document.createElement('button');
  btn.id = 'undo-placement-btn';
  btn.innerHTML = `<span id="undo-placement-icon">\u21A9</span><span>Undo</span>`;
  btn.addEventListener('click', (e) => {
    e.stopPropagation();
    performUndo();
  });

  const track = document.createElement('div');
  track.id = 'undo-countdown-track';

  countdownBar = document.createElement('div');
  countdownBar.id = 'undo-countdown-bar';

  track.appendChild(countdownBar);
  undoContainer.appendChild(btn);
  undoContainer.appendChild(track);
  document.body.appendChild(undoContainer);
}

// ---------------------------------------------------------------------------
// initUndoPlacement — called once from main.js
// ---------------------------------------------------------------------------
export function initUndoPlacement() {
  injectStyles();
  createDOM();

  eventBus.on(GameEvents.OBJECT_PLACED, (data) => {
    showUndoButton(data);
  });

  console.log('[UndoPlacement] Initialized');
}

// ---------------------------------------------------------------------------
// showUndoButton — display undo UI with countdown
// ---------------------------------------------------------------------------
function showUndoButton(data) {
  // Cancel any existing undo window
  hideUndoButton();

  // data contains { id, recipeId, position, mesh }
  if (!data.mesh) {
    console.warn('[UndoPlacement] OBJECT_PLACED event missing mesh reference, undo unavailable');
    return;
  }

  currentUndo = {
    mesh: data.mesh,
    recipeId: data.recipeId,
    id: data.id,
  };

  // Show the container
  undoContainer.classList.remove('undo-fading');
  undoContainer.classList.add('undo-visible');

  // Animate the countdown bar from 100% to 0%
  const startTime = performance.now();

  function updateBar(now) {
    const elapsed = now - startTime;
    const remaining = Math.max(0, 1 - elapsed / UNDO_DURATION_MS);
    countdownBar.style.transform = `scaleX(${remaining})`;

    if (remaining > 0 && currentUndo) {
      animationFrameId = requestAnimationFrame(updateBar);
    }
  }
  animationFrameId = requestAnimationFrame(updateBar);

  // Auto-hide after duration
  undoTimer = setTimeout(() => {
    hideUndoButton();
  }, UNDO_DURATION_MS);
}

// ---------------------------------------------------------------------------
// hideUndoButton — fade out and clean up
// ---------------------------------------------------------------------------
function hideUndoButton() {
  if (undoTimer) {
    clearTimeout(undoTimer);
    undoTimer = null;
  }

  if (animationFrameId) {
    cancelAnimationFrame(animationFrameId);
    animationFrameId = null;
  }

  currentUndo = null;

  if (undoContainer) {
    undoContainer.classList.remove('undo-visible');
    undoContainer.classList.add('undo-fading');

    // Reset bar after fade completes
    setTimeout(() => {
      if (countdownBar) {
        countdownBar.style.transform = 'scaleX(1)';
      }
      if (undoContainer) {
        undoContainer.classList.remove('undo-fading');
      }
    }, 300);
  }
}

// ---------------------------------------------------------------------------
// performUndo — reverse the placement
// ---------------------------------------------------------------------------
function performUndo() {
  if (!currentUndo) return;

  const { mesh, recipeId, id } = currentUndo;
  const recipe = getRecipe(recipeId);

  // 1. Remove mesh from scene
  if (mesh.parent) {
    mesh.parent.remove(mesh);
  }

  // Dispose geometry and materials to free GPU memory
  mesh.traverse((child) => {
    if (child.isMesh) {
      if (child.geometry) child.geometry.dispose();
      if (child.material) {
        if (Array.isArray(child.material)) {
          child.material.forEach((m) => m.dispose());
        } else {
          child.material.dispose();
        }
      }
    }
    // Remove any lights added by onPlace callbacks
    if (child.isLight) {
      if (child.parent) child.parent.remove(child);
    }
  });

  // 2. Remove from state.placedObjects
  const state = getGameState();
  if (state.placedObjects) {
    state.placedObjects = state.placedObjects.filter((o) => o.id !== id);
  }
  saveGameState(state);

  // 3. Refund resources
  if (recipe && recipe.cost) {
    for (const [type, amount] of Object.entries(recipe.cost)) {
      addToInventory(type, amount);
    }
    console.log(`[UndoPlacement] Refunded resources for ${recipeId}`);
  }

  // 4. Emit OBJECT_REMOVED event
  eventBus.emit(GameEvents.OBJECT_REMOVED, { recipeId, id });

  console.log(`[UndoPlacement] Undid placement of ${recipeId} (id: ${id})`);

  // 5. Hide the undo button
  hideUndoButton();
}
